<template>
  <div class="cms-settings">
    <cms-page-header
      :title="$t('cms.settings.title')"
      :show-create="false"
    />

    <div v-if="isLoading" class="cms-settings-loading">
      <div class="cms-loading-spinner"></div>
      <p>{{ $t('general.shared.loading') }}</p>
    </div>

    <form v-else @submit.prevent="saveSettings" class="cms-settings-form">
      <nav class="cms-settings-tabs" aria-label="CMS settings sections">
        <button
          type="button"
          class="cms-settings-tab"
          :class="{ active: activeSettingsTab === 'general' }"
          @click="activeSettingsTab = 'general'"
        >
          {{ $t('cms.settings.general.title') }}
        </button>
        <button
          type="button"
          class="cms-settings-tab"
          :class="{ active: activeSettingsTab === 'contact' }"
          @click="activeSettingsTab = 'contact'"
        >
          {{ $t('cms.settings.contact.title') }}
        </button>
        <button
          type="button"
          class="cms-settings-tab"
          :class="{ active: activeSettingsTab === 'social' }"
          @click="activeSettingsTab = 'social'"
        >
          {{ $t('cms.settings.social.title') }}
        </button>
        <button
          type="button"
          class="cms-settings-tab"
          :class="{ active: activeSettingsTab === 'trip' }"
          @click="activeSettingsTab = 'trip'"
        >
          {{ $t('cms.settings.tripAvailability.title') }}
        </button>
        <button
          type="button"
          class="cms-settings-tab"
          :class="{ active: activeSettingsTab === 'hero' }"
          @click="activeSettingsTab = 'hero'"
        >
          {{ $t('cms.settings.hero.title') }}
        </button>
        <button
          type="button"
          class="cms-settings-tab"
          :class="{ active: activeSettingsTab === 'appearance' }"
          @click="activeSettingsTab = 'appearance'"
        >
          {{ $t('cms.settings.appearance.title') }}
        </button>
        <button
          type="button"
          class="cms-settings-tab"
          :class="{ active: activeSettingsTab === 'maintenance' }"
          @click="activeSettingsTab = 'maintenance'"
        >
          {{ $t('cms.settings.maintenance.title') }}
        </button>
      </nav>

      <!-- General Settings -->
      <cms-form-section
        v-show="activeSettingsTab === 'general'"
        :title="$t('cms.settings.general.title')"
        :collapsible="false"
      >
        <div class="cms-settings-grid">
          <cms-input
            v-model="settings.site_name"
            :label="$t('cms.settings.general.siteName')"
            placeholder="Enter site name"
            :full-width="true"
          />
          <cms-input
            v-model="settings.site_url"
            :label="$t('cms.settings.general.siteUrl')"
            type="url"
            placeholder="https://example.com"
            :full-width="true"
          />
          <cms-textarea
            v-model="settings.site_description"
            :label="$t('cms.settings.general.siteDescription')"
            :rows="4"
            placeholder="Enter a brief description of your site"
            :full-width="true"
          />
        </div>
      </cms-form-section>

      <!-- Contact Information -->
      <cms-form-section
        v-show="activeSettingsTab === 'contact'"
        :title="$t('cms.settings.contact.title')"
        :collapsible="false"
      >
        <div class="cms-contact-layout">
          <div class="cms-contact-card">
            <div class="cms-contact-card-header">
              <h4>Primary Contact</h4>
              <p>How customers can directly reach you.</p>
            </div>
            <div class="cms-contact-grid">
              <cms-input
                v-model="settings.contact_email"
                :label="$t('cms.settings.contact.email')"
                type="email"
                placeholder="contact@example.com"
              />
              <cms-input
                v-model="settings.contact_phone"
                :label="$t('cms.settings.contact.phone')"
                type="tel"
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>

          <div class="cms-contact-card">
            <div class="cms-contact-card-header">
              <h4>Office Address</h4>
              <p>Displayed as your official location details.</p>
            </div>
            <cms-input
              v-model="settings.contact_address"
              :label="$t('cms.settings.contact.address')"
              placeholder="Street address"
              :full-width="true"
            />
            <div class="cms-address-group">
              <cms-input
                v-model="settings.contact_city"
                :label="$t('cms.settings.contact.city')"
                placeholder="City"
              />
              <cms-input
                v-model="settings.contact_postal_code"
                :label="$t('cms.settings.contact.postalCode')"
                placeholder="Postal code"
              />
            </div>
            <cms-input
              v-model="settings.contact_country"
              :label="$t('cms.settings.contact.country')"
              placeholder="Country"
              :full-width="true"
            />
          </div>
        </div>
      </cms-form-section>

      <!-- Social Media Links -->
      <cms-form-section
        v-show="activeSettingsTab === 'social'"
        :title="$t('cms.settings.social.title')"
        :collapsible="false"
      >
        <div class="cms-settings-grid">
          <div class="cms-social-input-wrapper">
            <label class="cms-social-label">{{ $t('cms.settings.social.facebook') }}</label>
            <div class="cms-social-input-container">
              <div class="cms-social-icon facebook">f</div>
              <cms-input
                v-model="settings.social_facebook"
                type="url"
                placeholder="https://facebook.com/yourpage"
                :full-width="true"
              />
            </div>
          </div>
          <div class="cms-social-input-wrapper">
            <label class="cms-social-label">{{ $t('cms.settings.social.instagram') }}</label>
            <div class="cms-social-input-container">
              <div class="cms-social-icon instagram">@</div>
              <cms-input
                v-model="settings.social_instagram"
                type="url"
                placeholder="https://instagram.com/yourpage"
                :full-width="true"
              />
            </div>
          </div>
          <div class="cms-social-input-wrapper">
            <label class="cms-social-label">{{ $t('cms.settings.social.twitter') }}</label>
            <div class="cms-social-input-container">
              <div class="cms-social-icon twitter">𝕏</div>
              <cms-input
                v-model="settings.social_twitter"
                type="url"
                placeholder="https://twitter.com/yourpage"
                :full-width="true"
              />
            </div>
          </div>
          <div class="cms-social-input-wrapper">
            <label class="cms-social-label">{{ $t('cms.settings.social.linkedin') }}</label>
            <div class="cms-social-input-container">
              <div class="cms-social-icon linkedin">in</div>
              <cms-input
                v-model="settings.social_linkedin"
                type="url"
                placeholder="https://linkedin.com/company/yourpage"
                :full-width="true"
              />
            </div>
          </div>
          <div class="cms-social-input-wrapper">
            <label class="cms-social-label">{{ $t('cms.settings.social.youtube') }}</label>
            <div class="cms-social-input-container">
              <div class="cms-social-icon youtube">▶</div>
              <cms-input
                v-model="settings.social_youtube"
                type="url"
                placeholder="https://youtube.com/channel/yourchannel"
                :full-width="true"
              />
            </div>
          </div>
        </div>
      </cms-form-section>

      <!-- Trip availability messages -->
      <cms-form-section
        v-show="activeSettingsTab === 'trip'"
        :title="$t('cms.settings.tripAvailability.title')"
        :collapsible="false"
      >
        <div class="cms-trip-availability">
          <div class="cms-trip-threshold-card">
            <div class="cms-trip-messages-header">
              <h4>{{ $t('cms.settings.tripAvailability.threshold') }}</h4>
              <p>{{ $t('cms.settings.tripAvailability.thresholdHelp') }}</p>
            </div>
            <cms-input
              v-model.number="settings.trip_slot_low_stock_threshold"
              :label="$t('cms.settings.tripAvailability.threshold')"
              type="number"
              min="1"
              max="100"
              :full-width="true"
            />
            <p class="cms-trip-threshold-preview">
              Low slot preview count: <strong>{{ Number(settings.trip_slot_low_stock_threshold) || 0 }}</strong>
            </p>
          </div>

          <div class="cms-trip-messages-card">
            <div class="cms-trip-messages-header">
              <h4>Promotional Messages</h4>
              <p>Edit one message type at a time without scrolling.</p>
            </div>

            <cms-translation-tabs :languages="promoLanguages" v-model="promoLanguage">
              <template #default="{}">
                <div class="cms-trip-message-type-tabs">
                  <button
                    type="button"
                    class="cms-trip-message-type-tab"
                    :class="{ active: tripMessageType === 'low_slot' }"
                    @click="tripMessageType = 'low_slot'"
                  >
                    Low slot
                  </button>
                  <button
                    type="button"
                    class="cms-trip-message-type-tab"
                    :class="{ active: tripMessageType === 'sold_out' }"
                    @click="tripMessageType = 'sold_out'"
                  >
                    Sold out
                  </button>
                  <button
                    type="button"
                    class="cms-trip-message-type-tab"
                    :class="{ active: tripMessageType === 'unavailable' }"
                    @click="tripMessageType = 'unavailable'"
                  >
                    Unavailable
                  </button>
                </div>

                <div class="cms-trip-messages-grid">
                  <cms-textarea
                    v-model="currentTripMessage"
                    :label="currentTripMessageLabel"
                    :rows="3"
                    :placeholder="tripMessageType === 'low_slot' ? $t('cms.settings.tripAvailability.placeholderCount') : ''"
                    :full-width="true"
                  />

                  <div class="cms-trip-preview-card">
                    <p class="cms-trip-preview-title">Message preview</p>
                    <div class="cms-trip-preview-list">
                      <p>
                        <strong>{{ tripMessageType === 'low_slot' ? 'Low slot' : tripMessageType === 'sold_out' ? 'Sold out' : 'Unavailable' }}:</strong>
                        {{
                          tripMessageType === 'low_slot'
                            ? (formatTripMessage(currentTripMessage, Number(settings.trip_slot_low_stock_threshold) || 3) || 'No message set')
                            : (currentTripMessage || 'No message set')
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </cms-translation-tabs>
          </div>
        </div>
      </cms-form-section>

      <!-- Appearance Settings -->
      <cms-form-section
        v-show="activeSettingsTab === 'hero'"
        :title="$t('cms.settings.hero.title')"
        :collapsible="false"
      >
        <div class="cms-hero-layout">
          <div class="cms-hero-card">
            <div class="cms-hero-card-header">
              <h4>{{ $t('cms.settings.hero.mediaType') }}</h4>
            </div>
            <div class="cms-hero-select">
              <label class="cms-color-label">{{ $t('cms.settings.hero.mediaType') }}</label>
              <select v-model="settings.hero_media_type" class="cms-select-input">
                <option value="none">{{ $t('cms.settings.hero.none') }}</option>
                <option value="image">{{ $t('cms.settings.hero.image') }}</option>
                <option value="video">{{ $t('cms.settings.hero.video') }}</option>
                <option value="youtube">{{ $t('cms.settings.hero.youtube') }}</option>
              </select>
            </div>

            <cms-input
              v-if="settings.hero_media_type === 'youtube'"
              v-model="settings.hero_youtube_url"
              :label="$t('cms.settings.hero.youtubeUrl')"
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              :full-width="true"
            />
            <div
              v-if="settings.hero_media_type === 'youtube' && settings.hero_youtube_url?.trim()"
              class="cms-hero-youtube-preview"
            >
              <a :href="settings.hero_youtube_url" target="_blank" rel="noopener noreferrer">
                {{ settings.hero_youtube_url }}
              </a>
            </div>
          </div>

          <div class="cms-hero-card" v-if="settings.hero_media_type !== 'none'">
            <div class="cms-hero-card-header">
              <h4>{{ settings.hero_media_type === 'image' ? $t('cms.settings.hero.imageUpload') : $t('cms.settings.hero.videoUpload') }}</h4>
            </div>

            <div v-if="settings.hero_media_type === 'image'" class="cms-media-field">
              <div class="cms-media-upload-container">
                <div v-if="settings.hero_image && settings.hero_image.trim()" class="cms-media-preview">
                  <div class="cms-media-preview-wrapper cms-hero-preview-wrapper">
                    <img :src="settings.hero_image" alt="Hero image" class="cms-media-preview-image cms-hero-preview" />
                    <button type="button" class="cms-media-remove-btn" @click="removeHeroImage" :title="$t('general.shared.remove')">
                      <v-icon icon="close" size="sm" />
                    </button>
                  </div>
                  <p class="cms-media-status">Uploaded</p>
                </div>
                <div v-else class="cms-media-upload-area cms-hero-upload-area">
                  <v-uploader
                    ref="heroImageUploader"
                    :initial-value="[]"
                    :multiple="false"
                    :max-items="1"
                    route-prefix="api/s3"
                    :after-upload="handleHeroImageUpload"
                    @removed="handleHeroImageRemove"
                  />
                </div>
              </div>
            </div>

            <div v-if="settings.hero_media_type === 'video'" class="cms-media-field">
              <div class="cms-media-upload-container">
                <div v-if="settings.hero_video && settings.hero_video.trim()" class="cms-media-preview">
                  <div class="cms-media-preview-wrapper cms-hero-preview-wrapper">
                    <video :src="settings.hero_video" class="cms-media-preview-image cms-hero-preview" muted controls playsinline />
                    <button type="button" class="cms-media-remove-btn" @click="removeHeroVideo" :title="$t('general.shared.remove')">
                      <v-icon icon="close" size="sm" />
                    </button>
                  </div>
                  <p class="cms-media-status">Uploaded</p>
                </div>
                <div v-else class="cms-media-upload-area cms-hero-upload-area">
                  <v-uploader
                    ref="heroVideoUploader"
                    :initial-value="[]"
                    :multiple="false"
                    :max-items="1"
                    route-prefix="api/s3"
                    :after-upload="handleHeroVideoUpload"
                    @removed="handleHeroVideoRemove"
                  />
                  <p class="cms-media-hint">{{ $t('cms.settings.hero.videoRecommendedSpec') }}</p>
                </div>
              </div>

              <div class="cms-hero-mobile-image">
                <p class="cms-media-hint cms-media-hint--block">{{ $t('cms.settings.hero.videoMobileImageHint') }}</p>
                <h5 class="cms-hero-subheading">{{ $t('cms.settings.hero.mobileImageUpload') }}</h5>
                <div class="cms-media-upload-container">
                  <div v-if="settings.hero_image && settings.hero_image.trim()" class="cms-media-preview">
                    <div class="cms-media-preview-wrapper cms-hero-preview-wrapper">
                      <img :src="settings.hero_image" alt="Hero mobile fallback image" class="cms-media-preview-image cms-hero-preview" />
                      <button type="button" class="cms-media-remove-btn" @click="removeHeroImage" :title="$t('general.shared.remove')">
                        <v-icon icon="close" size="sm" />
                      </button>
                    </div>
                    <p class="cms-media-status">Uploaded</p>
                  </div>
                  <div v-else class="cms-media-upload-area cms-hero-upload-area">
                    <v-uploader
                      ref="heroImageUploader"
                      :initial-value="[]"
                      :multiple="false"
                      :max-items="1"
                      route-prefix="api/s3"
                      :after-upload="handleHeroImageUpload"
                      @removed="handleHeroImageRemove"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="cms-hero-card">
            <div class="cms-hero-card-header">
              <h4>{{ $t('cms.settings.hero.textMode') }}</h4>
            </div>
            <div class="cms-hero-select">
              <label class="cms-color-label">{{ $t('cms.settings.hero.textMode') }}</label>
              <select v-model="settings.hero_text_mode" class="cms-select-input">
                <option value="auto">{{ $t('cms.settings.hero.textModeAuto') }}</option>
                <option value="manual">{{ $t('cms.settings.hero.textModeManual') }}</option>
              </select>
            </div>

            <label class="cms-toggle-field cms-toggle-field--card">
              <input
                v-model="settings.hero_text_visible"
                :disabled="settings.hero_text_mode !== 'manual'"
                type="checkbox"
              />
              <span>{{ $t('cms.settings.hero.textVisible') }}</span>
            </label>

            <label class="cms-toggle-field cms-toggle-field--card">
              <input v-model="settings.hero_overlay_enabled" type="checkbox" />
              <span>{{ $t('cms.settings.hero.overlayEnabled') }}</span>
            </label>

            <cms-input
              v-model.number="settings.hero_overlay_opacity"
              :label="$t('cms.settings.hero.overlayOpacity')"
              type="number"
              min="0"
              max="100"
              :full-width="true"
            />
          </div>
        </div>
      </cms-form-section>

      <cms-form-section
        v-show="activeSettingsTab === 'appearance'"
        :title="$t('cms.settings.appearance.title')"
        :collapsible="false"
      >
        <div class="cms-appearance-grid">
          <!-- Colors Section -->
          <div class="cms-colors-section">
            <div class="cms-color-field">
              <label class="cms-color-label">{{ $t('cms.settings.appearance.primaryColor') }}</label>
              <div class="cms-color-picker-wrapper">
                <input
                  v-model="settings.primary_color"
                  type="color"
                  class="cms-color-input"
                />
                <div class="cms-color-display" :style="{ backgroundColor: settings.primary_color || '#000000' }"></div>
                <cms-input
                  v-model="settings.primary_color"
                  type="text"
                  placeholder="#000000"
                />
              </div>
              <p v-if="!settings.primary_color || !settings.primary_color.trim()" class="cms-field-hint">{{ $t('cms.settings.appearance.defaultAppearance') }}</p>
            </div>

            <div class="cms-color-field">
              <label class="cms-color-label">{{ $t('cms.settings.appearance.secondaryColor') }}</label>
              <div class="cms-color-picker-wrapper">
                <input
                  v-model="settings.secondary_color"
                  type="color"
                  class="cms-color-input"
                />
                <div class="cms-color-display" :style="{ backgroundColor: settings.secondary_color || '#2D2D2D' }"></div>
                <cms-input
                  v-model="settings.secondary_color"
                  type="text"
                  placeholder="#2D2D2D"
                />
              </div>
              <p v-if="!settings.secondary_color || !settings.secondary_color.trim()" class="cms-field-hint">{{ $t('cms.settings.appearance.defaultAppearance') }}</p>
            </div>
          </div>

          <!-- Logo Upload -->
          <div class="cms-media-field">
            <label class="cms-media-label">{{ $t('cms.settings.appearance.logo') }}</label>
            <div class="cms-media-upload-container">
              <div v-if="settings.logo && settings.logo.trim()" class="cms-media-preview">
                <div class="cms-media-preview-wrapper">
                  <img :src="settings.logo" alt="Logo" class="cms-media-preview-image cms-logo-preview" />
                  <button type="button" class="cms-media-remove-btn" @click="removeLogo" :title="$t('general.shared.remove')">
                    <v-icon icon="close" size="sm" />
                  </button>
                </div>
                <p class="cms-media-status">Uploaded</p>
              </div>
              <div v-else class="cms-media-upload-area">
                <v-uploader
                  ref="logoUploader"
                  :initial-value="[]"
                  :multiple="false"
                  :max-items="1"
                  route-prefix="api/s3"
                  :after-upload="handleLogoUpload"
                  @removed="handleLogoRemove"
                />
                <p class="cms-media-hint">{{ $t('cms.settings.appearance.defaultLogo') }}</p>
              </div>
            </div>
          </div>

          <!-- Favicon Upload -->
          <div class="cms-media-field">
            <label class="cms-media-label">{{ $t('cms.settings.appearance.favicon') }}</label>
            <div class="cms-media-upload-container">
              <div v-if="settings.favicon && settings.favicon.trim()" class="cms-media-preview">
                <div class="cms-media-preview-wrapper cms-favicon-wrapper">
                  <img :src="settings.favicon" alt="Favicon" class="cms-media-preview-image cms-favicon-preview" />
                  <button type="button" class="cms-media-remove-btn" @click="removeFavicon" :title="$t('general.shared.remove')">
                    <v-icon icon="close" size="sm" />
                  </button>
                </div>
                <p class="cms-media-status">Uploaded</p>
              </div>
              <div v-else class="cms-media-upload-area">
                <v-uploader
                  ref="faviconUploader"
                  :initial-value="[]"
                  :multiple="false"
                  :max-items="1"
                  route-prefix="api/s3"
                  :after-upload="handleFaviconUpload"
                  @removed="handleFaviconRemove"
                />
                <p class="cms-media-hint">{{ $t('cms.settings.appearance.defaultFavicon') }}</p>
              </div>
            </div>
          </div>
        </div>
      </cms-form-section>

      <!-- Maintenance Mode -->
      <cms-form-section
        v-show="activeSettingsTab === 'maintenance'"
        :title="$t('cms.settings.maintenance.title')"
        :collapsible="false"
      >
        <div class="cms-maintenance-layout">
          <div class="cms-maintenance-warning" :class="{ active: settings.maintenance_enabled }">
            <p>
              <strong>{{ settings.maintenance_enabled ? $t('cms.settings.maintenance.statusOn') : $t('cms.settings.maintenance.statusOff') }}</strong>
              — {{ $t('cms.settings.maintenance.warning') }}
            </p>
          </div>

          <label class="cms-toggle-field cms-toggle-field--card cms-maintenance-toggle">
            <input v-model="settings.maintenance_enabled" type="checkbox" />
            <span>{{ $t('cms.settings.maintenance.enabled') }}</span>
          </label>

          <cms-translation-tabs :languages="promoLanguages" v-model="promoLanguage">
            <template #default="{}">
              <div class="cms-maintenance-fields">
                <cms-input
                  v-model="currentMaintenanceTitle"
                  :label="promoLanguage === 'en' ? $t('cms.settings.maintenance.titleEn') : $t('cms.settings.maintenance.titleNl')"
                  :full-width="true"
                />
                <cms-textarea
                  v-model="currentMaintenanceMessage"
                  :label="promoLanguage === 'en' ? $t('cms.settings.maintenance.messageEn') : $t('cms.settings.maintenance.messageNl')"
                  :rows="4"
                  :full-width="true"
                />
              </div>
            </template>
          </cms-translation-tabs>

          <div class="cms-maintenance-bypass-card">
            <div class="cms-maintenance-bypass-header">
              <h4>{{ $t('cms.settings.maintenance.bypassTitle') }}</h4>
              <p>{{ $t('cms.settings.maintenance.bypassHint') }}</p>
            </div>
            <div class="cms-maintenance-bypass-row">
              <cms-input
                v-model="settings.maintenance_bypass_token"
                :label="$t('cms.settings.maintenance.bypassToken')"
                :full-width="true"
              />
              <div class="cms-maintenance-bypass-actions">
                <cms-button type="button" variant="secondary" @click="copyBypassToken">
                  {{ $t('cms.settings.maintenance.copyToken') }}
                </cms-button>
                <cms-button type="button" variant="secondary" :disabled="isRegeneratingToken" @click="regenerateBypassToken">
                  {{ $t('cms.settings.maintenance.regenerateToken') }}
                </cms-button>
              </div>
            </div>
            <p class="cms-field-hint">{{ $t('cms.settings.maintenance.envHint') }}</p>
          </div>
        </div>
      </cms-form-section>
    </form>

    <!-- Sticky Save Button -->
    <div class="cms-settings-save-bar">
      <div class="cms-settings-save-content">
        <p class="cms-settings-save-note" :class="{ 'is-dirty': hasUnsavedChanges }">
          <v-icon :icon="hasUnsavedChanges ? 'alert-circle' : 'check-circle'" size="sm" />
          {{ saveStatusText }}
        </p>
        <cms-button
          variant="primary"
          icon="download"
          :disabled="isSaving || isLoading || !hasUnsavedChanges"
          @click="saveSettings"
          class="cms-settings-save-button"
        >
          {{ isSaving ? $t('general.button.saving') : $t('cms.settings.save') }}
        </cms-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import api from '@/api';
import {
  CmsPageHeader,
  CmsFormSection,
  CmsInput,
  CmsTextarea,
  CmsButton,
  CmsTranslationTabs,
} from '@/components/cms';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VUploader from '@/components/vendor/upload/VUploader.vue';

interface Settings {
  id: number;
  site_name: string;
  site_description: string;
  site_url: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  contact_city: string;
  contact_postal_code: string;
  contact_country: string;
  social_facebook: string;
  social_instagram: string;
  social_twitter: string;
  social_linkedin: string;
  social_youtube: string;
  primary_color: string;
  secondary_color: string;
  logo: string;
  favicon: string;
  trip_slot_low_stock_threshold: number;
  trip_slot_low_stock_message_en: string;
  trip_slot_low_stock_message_nl: string;
  trip_slot_sold_out_message_en: string;
  trip_slot_sold_out_message_nl: string;
  trip_slot_unavailable_message_en: string;
  trip_slot_unavailable_message_nl: string;
  hero_media_type: 'none' | 'image' | 'video' | 'youtube';
  hero_image: string;
  hero_video: string;
  hero_youtube_url: string;
  hero_text_mode: 'auto' | 'manual';
  hero_text_visible: boolean;
  hero_overlay_enabled: boolean;
  hero_overlay_opacity: number;
  maintenance_enabled: boolean;
  maintenance_title_en: string;
  maintenance_title_nl: string;
  maintenance_message_en: string;
  maintenance_message_nl: string;
  maintenance_bypass_token: string;
}

type SettingsTab = 'general' | 'contact' | 'social' | 'trip' | 'hero' | 'appearance' | 'maintenance';
type TripMessageType = 'low_slot' | 'sold_out' | 'unavailable';

export default defineComponent({
  name: 'TheCmsSettings',
  components: {
    CmsPageHeader,
    CmsFormSection,
    CmsInput,
    CmsTextarea,
    CmsButton,
    CmsTranslationTabs,
    VIcon,
    VUploader,
  },
  setup() {
    const toast = useToast();
    const { t } = useI18n();
    const route = useRoute();
    const authStore = useAuthStore();
    const isSaving = ref(false);
    const isLoading = ref(false);
    const isRegeneratingToken = ref(false);
    const activeSettingsTab = ref<SettingsTab>('general');
    const promoLanguage = ref('en');
    const tripMessageType = ref<TripMessageType>('low_slot');
    const promoLanguages = [
      { key: 'en', shortName: 'en', label: 'English' },
      { key: 'nl', shortName: 'nl', label: 'Dutch' },
    ];
    const lastSavedSnapshot = ref('');

    const agencySlug = computed(() => authStore.agency?.slug ?? null);

    const logoUploader = ref<any>(null);
    const faviconUploader = ref<any>(null);
    const heroImageUploader = ref<any>(null);
    const heroVideoUploader = ref<any>(null);

    const settings = reactive<Settings>({
      id: 0,
      site_name: '',
      site_description: '',
      site_url: '',
      contact_email: '',
      contact_phone: '',
      contact_address: '',
      contact_city: '',
      contact_postal_code: '',
      contact_country: '',
      social_facebook: '',
      social_instagram: '',
      social_twitter: '',
      social_linkedin: '',
      social_youtube: '',
      primary_color: '',
      secondary_color: '',
      logo: '',
      favicon: '',
      trip_slot_low_stock_threshold: 3,
      trip_slot_low_stock_message_en: '',
      trip_slot_low_stock_message_nl: '',
      trip_slot_sold_out_message_en: '',
      trip_slot_sold_out_message_nl: '',
      trip_slot_unavailable_message_en: '',
      trip_slot_unavailable_message_nl: '',
      hero_media_type: 'none',
      hero_image: '',
      hero_video: '',
      hero_youtube_url: '',
      hero_text_mode: 'auto',
      hero_text_visible: true,
      hero_overlay_enabled: true,
      hero_overlay_opacity: 45,
      maintenance_enabled: false,
      maintenance_title_en: '',
      maintenance_title_nl: '',
      maintenance_message_en: '',
      maintenance_message_nl: '',
      maintenance_bypass_token: '',
    });

    const fieldTabMap: Record<string, SettingsTab> = {
      site_name: 'general',
      site_description: 'general',
      site_url: 'general',
      contact_email: 'contact',
      contact_phone: 'contact',
      contact_address: 'contact',
      contact_city: 'contact',
      contact_postal_code: 'contact',
      contact_country: 'contact',
      social_facebook: 'social',
      social_instagram: 'social',
      social_twitter: 'social',
      social_linkedin: 'social',
      social_youtube: 'social',
      trip_slot_low_stock_threshold: 'trip',
      trip_slot_low_stock_message: 'trip',
      trip_slot_sold_out_message: 'trip',
      trip_slot_unavailable_message: 'trip',
      hero_media_type: 'hero',
      hero_image: 'hero',
      hero_video: 'hero',
      hero_youtube_url: 'hero',
      hero_text_mode: 'hero',
      hero_text_visible: 'hero',
      hero_overlay_enabled: 'hero',
      hero_overlay_opacity: 'hero',
      primary_color: 'appearance',
      secondary_color: 'appearance',
      logo: 'appearance',
      favicon: 'appearance',
      maintenance_enabled: 'maintenance',
      maintenance_title: 'maintenance',
      maintenance_message: 'maintenance',
      maintenance_bypass_token: 'maintenance',
    };

    const switchToFirstFieldTab = (errorData: any) => {
      const validationErrors = errorData?.errors;
      const errorKeys = validationErrors && typeof validationErrors === 'object'
        ? Object.keys(validationErrors)
        : [];
      const firstKey = errorKeys[0] || '';
      const directMatch = fieldTabMap[firstKey];

      if (directMatch) {
        activeSettingsTab.value = directMatch;
        return;
      }

      const fallbackMatch = Object.keys(fieldTabMap).find((field) => firstKey.includes(field));
      if (fallbackMatch) {
        activeSettingsTab.value = fieldTabMap[fallbackMatch];
      }
    };

    const formatTripMessage = (message: string, count: number) => {
      if (!message) {
        return '';
      }
      return message.replace(/\{count\}/g, String(count));
    };

    const currentTripMessage = computed({
      get: () => {
        const isEn = promoLanguage.value === 'en';
        if (tripMessageType.value === 'low_slot') {
          return isEn ? settings.trip_slot_low_stock_message_en : settings.trip_slot_low_stock_message_nl;
        }
        if (tripMessageType.value === 'sold_out') {
          return isEn ? settings.trip_slot_sold_out_message_en : settings.trip_slot_sold_out_message_nl;
        }
        return isEn ? settings.trip_slot_unavailable_message_en : settings.trip_slot_unavailable_message_nl;
      },
      set: (value: string) => {
        const isEn = promoLanguage.value === 'en';
        if (tripMessageType.value === 'low_slot') {
          if (isEn) {
            settings.trip_slot_low_stock_message_en = value;
          } else {
            settings.trip_slot_low_stock_message_nl = value;
          }
          return;
        }
        if (tripMessageType.value === 'sold_out') {
          if (isEn) {
            settings.trip_slot_sold_out_message_en = value;
          } else {
            settings.trip_slot_sold_out_message_nl = value;
          }
          return;
        }
        if (isEn) {
          settings.trip_slot_unavailable_message_en = value;
        } else {
          settings.trip_slot_unavailable_message_nl = value;
        }
      },
    });

    const currentTripMessageLabel = computed(() => {
      const isEn = promoLanguage.value === 'en';
      if (tripMessageType.value === 'low_slot') {
        return isEn ? t('cms.settings.tripAvailability.lowStockEn') : t('cms.settings.tripAvailability.lowStockNl');
      }
      if (tripMessageType.value === 'sold_out') {
        return isEn ? t('cms.settings.tripAvailability.soldOutEn') : t('cms.settings.tripAvailability.soldOutNl');
      }
      return isEn ? t('cms.settings.tripAvailability.unavailableEn') : t('cms.settings.tripAvailability.unavailableNl');
    });

    const currentMaintenanceTitle = computed({
      get: () => (promoLanguage.value === 'en' ? settings.maintenance_title_en : settings.maintenance_title_nl),
      set: (value: string) => {
        if (promoLanguage.value === 'en') {
          settings.maintenance_title_en = value;
        } else {
          settings.maintenance_title_nl = value;
        }
      },
    });

    const currentMaintenanceMessage = computed({
      get: () => (promoLanguage.value === 'en' ? settings.maintenance_message_en : settings.maintenance_message_nl),
      set: (value: string) => {
        if (promoLanguage.value === 'en') {
          settings.maintenance_message_en = value;
        } else {
          settings.maintenance_message_nl = value;
        }
      },
    });

    const buildSettingsPayload = () => ({
      site_name: settings.site_name?.trim() || null,
      site_description: settings.site_description?.trim() || null,
      site_url: settings.site_url?.trim() || null,
      contact_email: settings.contact_email?.trim() || null,
      contact_phone: settings.contact_phone?.trim() || null,
      contact_address: settings.contact_address?.trim() || null,
      contact_city: settings.contact_city?.trim() || null,
      contact_postal_code: settings.contact_postal_code?.trim() || null,
      contact_country: settings.contact_country?.trim() || null,
      social_facebook: settings.social_facebook?.trim() || null,
      social_instagram: settings.social_instagram?.trim() || null,
      social_twitter: settings.social_twitter?.trim() || null,
      social_linkedin: settings.social_linkedin?.trim() || null,
      social_youtube: settings.social_youtube?.trim() || null,
      primary_color: settings.primary_color?.trim() || null,
      secondary_color: settings.secondary_color?.trim() || null,
      logo: settings.logo?.trim() || null,
      favicon: settings.favicon?.trim() || null,
      trip_slot_low_stock_threshold: Number(settings.trip_slot_low_stock_threshold) || 3,
      trip_slot_low_stock_message: {
        en: settings.trip_slot_low_stock_message_en?.trim() || null,
        nl: settings.trip_slot_low_stock_message_nl?.trim() || null,
      },
      trip_slot_sold_out_message: {
        en: settings.trip_slot_sold_out_message_en?.trim() || null,
        nl: settings.trip_slot_sold_out_message_nl?.trim() || null,
      },
      trip_slot_unavailable_message: {
        en: settings.trip_slot_unavailable_message_en?.trim() || null,
        nl: settings.trip_slot_unavailable_message_nl?.trim() || null,
      },
      hero_media_type: settings.hero_media_type || 'none',
      hero_image: settings.hero_image?.trim() || null,
      hero_video: settings.hero_video?.trim() || null,
      hero_youtube_url: settings.hero_youtube_url?.trim() || null,
      hero_text_mode: settings.hero_text_mode || 'auto',
      hero_text_visible: Boolean(settings.hero_text_visible),
      hero_overlay_enabled: Boolean(settings.hero_overlay_enabled),
      hero_overlay_opacity: Number(settings.hero_overlay_opacity ?? 45),
      maintenance_enabled: Boolean(settings.maintenance_enabled),
      maintenance_title: {
        en: settings.maintenance_title_en?.trim() || null,
        nl: settings.maintenance_title_nl?.trim() || null,
      },
      maintenance_message: {
        en: settings.maintenance_message_en?.trim() || null,
        nl: settings.maintenance_message_nl?.trim() || null,
      },
      maintenance_bypass_token: settings.maintenance_bypass_token?.trim() || null,
    });

    const hasUnsavedChanges = computed(() => {
      const currentSnapshot = JSON.stringify(buildSettingsPayload());
      return currentSnapshot !== lastSavedSnapshot.value;
    });

    const saveStatusText = computed(() => {
      if (isSaving.value) {
        return 'Saving changes...';
      }
      if (hasUnsavedChanges.value) {
        return 'You have unsaved changes.';
      }
      return 'All changes saved.';
    });

    const loadSettings = async () => {
      if (!agencySlug.value) {
        return;
      }

      isLoading.value = true;
      try {
        const response = await api.settingsService.getCmsSettings(agencySlug.value);

        const data = response?.data || response || {};
        
        // Map API response to settings
        settings.id = data.id;
        settings.site_name = data.siteName || '';
        settings.site_description = data.siteDescription || '';
        settings.site_url = data.siteUrl || '';
        settings.contact_email = data.contactEmail || '';
        settings.contact_phone = data.contactPhone || '';
        settings.contact_address = data.contactAddress || '';
        settings.contact_city = data.contactCity || '';
        settings.contact_postal_code = data.contactPostalCode || '';
        settings.contact_country = data.contactCountry || '';
        settings.social_facebook = data.socialFacebook || '';
        settings.social_instagram = data.socialInstagram || '';
        settings.social_twitter = data.socialTwitter || '';
        settings.social_linkedin = data.socialLinkedin || '';
        settings.social_youtube = data.socialYoutube || '';
        settings.primary_color = data.primaryColor || '';
        settings.secondary_color = data.secondaryColor || '';
        settings.logo = data.logo || '';
        settings.favicon = data.favicon || '';

        const lowStock =
          data.tripSlotLowStockMessage || data.trip_slot_low_stock_message || {};
        const soldOut =
          data.tripSlotSoldOutMessage || data.trip_slot_sold_out_message || {};
        const unavailable =
          data.tripSlotUnavailableMessage || data.trip_slot_unavailable_message || {};

        settings.trip_slot_low_stock_threshold =
          data.tripSlotLowStockThreshold ?? data.trip_slot_low_stock_threshold ?? 3;
        settings.trip_slot_low_stock_message_en = lowStock.en || '';
        settings.trip_slot_low_stock_message_nl = lowStock.nl || '';
        settings.trip_slot_sold_out_message_en = soldOut.en || '';
        settings.trip_slot_sold_out_message_nl = soldOut.nl || '';
        settings.trip_slot_unavailable_message_en = unavailable.en || '';
        settings.trip_slot_unavailable_message_nl = unavailable.nl || '';
        settings.hero_media_type = data.heroMediaType || data.hero_media_type || 'none';
        settings.hero_image = data.heroImage || data.hero_image || '';
        settings.hero_video = data.heroVideo || data.hero_video || '';
        settings.hero_youtube_url = data.heroYoutubeUrl || data.hero_youtube_url || '';
        settings.hero_text_mode = data.heroTextMode || data.hero_text_mode || 'auto';
        settings.hero_text_visible = data.heroTextVisible ?? data.hero_text_visible ?? true;
        settings.hero_overlay_enabled = data.heroOverlayEnabled ?? data.hero_overlay_enabled ?? true;
        settings.hero_overlay_opacity = data.heroOverlayOpacity ?? data.hero_overlay_opacity ?? 45;

        const maintenanceTitle =
          data.maintenanceTitle || data.maintenance_title || {};
        const maintenanceMessage =
          data.maintenanceMessage || data.maintenance_message || {};

        settings.maintenance_enabled = Boolean(
          data.maintenanceEnabled ?? data.maintenance_enabled ?? false
        );
        settings.maintenance_title_en = maintenanceTitle.en || '';
        settings.maintenance_title_nl = maintenanceTitle.nl || '';
        settings.maintenance_message_en = maintenanceMessage.en || '';
        settings.maintenance_message_nl = maintenanceMessage.nl || '';
        settings.maintenance_bypass_token =
          data.maintenanceBypassToken || data.maintenance_bypass_token || '';

        lastSavedSnapshot.value = JSON.stringify(buildSettingsPayload());
      } catch (error: any) {
        console.error('Error loading settings', error);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isLoading.value = false;
      }
    };

    const buildMediaConfirmEntries = (mediaItems: any[] = [], collection: string) => {
      return (Array.isArray(mediaItems) ? mediaItems : [])
        .map((item: any, index: number) => {
          const attributes = item?.attributes ?? {};
          if (attributes.isExisting) {
            return null;
          }
          const uuid = attributes.uuid ?? item.uuid;
          if (!uuid) {
            return null;
          }
          const order = attributes.order ?? attributes.order_column ?? item.order ?? index + 1;
          return {
            uuid,
            name: attributes.name ?? item.name ?? `media-${order}`,
            order,
            collection,
          };
        })
        .filter((entry): entry is { uuid: string; name: string; order: number; collection: string } => !!entry);
    };

    const handleLogoUpload = async (media: any) => {
      // Transform media to the correct format
      const mediaArray = Array.isArray(media) ? media : [media];
      const mediaEntries = buildMediaConfirmEntries(mediaArray, 'logo');
      
      if (mediaEntries.length === 0) {
        console.error('No valid media entries to confirm');
        return;
      }

      // Get the URL from the uploaded media for preview
      const mediaUrl = mediaArray[0]?.original_url || mediaArray[0]?.preview_url || mediaArray[0]?.url || '';
      if (mediaUrl) {
        settings.logo = mediaUrl;
      }

      const response = await api.media.confirm('s3', {
        media: mediaEntries,
        model_type: 'cms_settings',
        model_id: settings.id,
        collection: 'logo',
      });
      const confirmedMedia = Array.isArray(response) ? response : [response];
      settings.logo = confirmedMedia[0]?.originalUrl || settings.logo;
    };

    const handleLogoRemove = () => {
      settings.logo = '';
    };

    const removeLogo = () => {
      settings.logo = '';
      if (logoUploader.value?.mediaLibrary) {
        logoUploader.value.mediaLibrary.state.media = [];
      }
    };

    const handleFaviconUpload = async (media: any) => {      
      // Transform media to the correct format
      const mediaArray = Array.isArray(media) ? media : [media];
      const mediaEntries = buildMediaConfirmEntries(mediaArray, 'favicon');
      
      if (mediaEntries.length === 0) {
        console.error('No valid media entries to confirm');
        return;
      }

      // Get the URL from the uploaded media for preview
      const mediaUrl = mediaArray[0]?.original_url || mediaArray[0]?.preview_url || mediaArray[0]?.url || '';
      if (mediaUrl) {
        settings.favicon = mediaUrl;
      }

      const response = await api.media.confirm('s3', {
        media: mediaEntries,
        model_type: 'cms_settings',
        model_id: settings.id,
        collection: 'favicon',
      });
      const confirmedMedia = Array.isArray(response) ? response : [response];
      settings.favicon = confirmedMedia[0]?.originalUrl || settings.favicon;
    };

    const handleFaviconRemove = () => {
      settings.favicon = '';
    };

    const removeFavicon = () => {
      settings.favicon = '';
      if (faviconUploader.value?.mediaLibrary) {
        faviconUploader.value.mediaLibrary.state.media = [];
      }
    };

    const handleHeroImageUpload = async (media: any) => {
      const mediaArray = Array.isArray(media) ? media : [media];
      const mediaEntries = buildMediaConfirmEntries(mediaArray, 'hero_image');

      if (mediaEntries.length === 0) {
        console.error('No valid media entries to confirm');
        return;
      }

      const mediaUrl = mediaArray[0]?.original_url || mediaArray[0]?.preview_url || mediaArray[0]?.url || '';
      if (mediaUrl) {
        settings.hero_image = mediaUrl;
      }

      const response = await api.media.confirm('s3', {
        media: mediaEntries,
        model_type: 'cms_settings',
        model_id: settings.id,
        collection: 'hero_image',
      });
      const confirmedMedia = Array.isArray(response) ? response : [response];
      settings.hero_image = confirmedMedia[0]?.originalUrl || settings.hero_image;
    };

    const handleHeroImageRemove = () => {
      settings.hero_image = '';
    };

    const removeHeroImage = () => {
      settings.hero_image = '';
      if (heroImageUploader.value?.mediaLibrary) {
        heroImageUploader.value.mediaLibrary.state.media = [];
      }
    };

    const handleHeroVideoUpload = async (media: any) => {
      const mediaArray = Array.isArray(media) ? media : [media];
      const mediaEntries = buildMediaConfirmEntries(mediaArray, 'hero_video');

      if (mediaEntries.length === 0) {
        console.error('No valid media entries to confirm');
        return;
      }

      const mediaUrl = mediaArray[0]?.original_url || mediaArray[0]?.preview_url || mediaArray[0]?.url || '';
      if (mediaUrl) {
        settings.hero_video = mediaUrl;
      }

      const response = await api.media.confirm('s3', {
        media: mediaEntries,
        model_type: 'cms_settings',
        model_id: settings.id,
        collection: 'hero_video',
      });
      const confirmedMedia = Array.isArray(response) ? response : [response];
      settings.hero_video = confirmedMedia[0]?.originalUrl || settings.hero_video;
    };

    const handleHeroVideoRemove = () => {
      settings.hero_video = '';
    };

    const removeHeroVideo = () => {
      settings.hero_video = '';
      if (heroVideoUploader.value?.mediaLibrary) {
        heroVideoUploader.value.mediaLibrary.state.media = [];
      }
    };

    const copyBypassToken = async () => {
      const token = settings.maintenance_bypass_token;
      if (!token) {
        toast.error(t('cms.settings.maintenance.noToken'));
        return;
      }
      try {
        await navigator.clipboard.writeText(token);
        toast.success(t('cms.settings.maintenance.tokenCopied'));
      } catch {
        toast.error(t('general.errorMessage'));
      }
    };

    const regenerateBypassToken = async () => {
      if (!agencySlug.value) {
        toast.error(t('general.errorMessage'));
        return;
      }

      isRegeneratingToken.value = true;
      try {
        const response = await api.settingsService.updateCmsSettings(agencySlug.value, {
          ...buildSettingsPayload(),
          regenerate_maintenance_bypass_token: true,
        });
        const data = response?.data || response || {};
        settings.maintenance_bypass_token =
          data.maintenanceBypassToken || data.maintenance_bypass_token || settings.maintenance_bypass_token;
        lastSavedSnapshot.value = JSON.stringify(buildSettingsPayload());
        toast.success(t('cms.settings.maintenance.tokenRegenerated'));
      } catch (error: any) {
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isRegeneratingToken.value = false;
      }
    };

    const saveSettings = async () => {
      if (!agencySlug.value) {
        toast.error(t('general.errorMessage'));
        return;
      }

      if (Number(settings.trip_slot_low_stock_threshold) < 1 || Number(settings.trip_slot_low_stock_threshold) > 100) {
        activeSettingsTab.value = 'trip';
        toast.error('Low slot threshold must be between 1 and 100.');
        return;
      }

      if (Number(settings.hero_overlay_opacity) < 0 || Number(settings.hero_overlay_opacity) > 100) {
        activeSettingsTab.value = 'hero';
        toast.error('Hero overlay opacity must be between 0 and 100.');
        return;
      }

      isSaving.value = true;
      try {
        const payload = buildSettingsPayload();
        await api.settingsService.updateCmsSettings(agencySlug.value, payload);
        lastSavedSnapshot.value = JSON.stringify(payload);
        toast.success(t('general.shared.savedChanges'));
      } catch (error: any) {
        console.error('Error saving settings', error);
        switchToFirstFieldTab(error?.response?.data);
        const message = error?.response?.data?.message || t('general.errorMessage');
        toast.error(message);
      } finally {
        isSaving.value = false;
      }
    };

    onMounted(() => {
      const tab = typeof route.query.tab === 'string' ? route.query.tab : '';
      if (['general', 'contact', 'social', 'trip', 'hero', 'appearance', 'maintenance'].includes(tab)) {
        activeSettingsTab.value = tab as SettingsTab;
      }
      if (agencySlug.value) {
        loadSettings();
      }
    });

    return {
      settings,
      isSaving,
      isLoading,
      isRegeneratingToken,
      activeSettingsTab,
      tripMessageType,
      currentTripMessage,
      currentTripMessageLabel,
      currentMaintenanceTitle,
      currentMaintenanceMessage,
      hasUnsavedChanges,
      saveStatusText,
      promoLanguage,
      promoLanguages,
      saveSettings,
      copyBypassToken,
      regenerateBypassToken,
      logoUploader,
      faviconUploader,
      heroImageUploader,
      heroVideoUploader,
      handleLogoUpload,
      handleLogoRemove,
      removeLogo,
      handleFaviconUpload,
      handleFaviconRemove,
      removeFavicon,
      handleHeroImageUpload,
      handleHeroImageRemove,
      removeHeroImage,
      handleHeroVideoUpload,
      handleHeroVideoRemove,
      removeHeroVideo,
      formatTripMessage,
    };
  }
});
</script>

<style lang="scss" scoped>
@import '@/components/cms/cms-styles.scss';

.cms-settings {
  color: $cms-text-primary;
}

.cms-settings-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 1rem;
}

.cms-settings-tabs {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  border: 1px solid $cms-border-color;
  border-radius: 12px;
}

.cms-settings-tab {
  border: 1px solid $cms-border-color;
  background: #fff;
  color: $cms-text-primary;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $cms-primary-color;
    color: $cms-primary-color;
    background: rgba($cms-primary-color, 0.06);
  }

  &.active {
    border-color: $cms-primary-color;
    background: $cms-primary-color;
    color: #fff;
    box-shadow: 0 6px 16px rgba($cms-primary-color, 0.25);
  }
}

// Loading State
.cms-settings-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1rem;
  color: $cms-text-secondary;

  p {
    margin: 0;
    font-size: 1rem;
  }
}

.cms-loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top-color: $cms-primary-color;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// Form Grid Layout
.cms-settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

// Address Group (City + Postal Code side by side)
.cms-address-group {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.cms-contact-layout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1rem;
}

.cms-contact-card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid $cms-border-color;
  border-radius: 12px;
  background: #fff;
}

.cms-contact-card-header {
  h4 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: $cms-text-primary;
  }

  p {
    margin: 0.3rem 0 0 0;
    font-size: 0.8rem;
    color: $cms-text-secondary;
  }
}

.cms-contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

// Social Media Inputs
.cms-social-input-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .cms-social-label {
    font-size: $cms-font-size-base;
    opacity: 0.5;
    font-weight: 400;
    color: $cms-text-primary;
    margin: 0;
  }

  .cms-social-input-container {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    .cms-social-icon {
      position: absolute;
      left: 0.5rem;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: $cms-radius-sm;
      flex-shrink: 0;
      transition: all $cms-transition-fast;
      font-weight: 600;
      font-size: 0.75rem;
      color: white;
      box-shadow: $cms-shadow-sm;
      pointer-events: none;

      &.facebook {
        background: #1877f2;
      }

      &.instagram {
        background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
      }

      &.twitter {
        background: #1da1f2;
      }

      &.linkedin {
        background: #0077b5;
      }

      &.youtube {
        background: #ff0000;
      }
    }

    :deep(.cms-form-group) {
      flex: 1;
      margin-bottom: 0;
      width: 100%;
    }

    :deep(.cms-input) {
      padding-left: 2.75rem;
      width: 100%;
    }

    // Ensure focus state works properly with icon
    :deep(.cms-input:focus) {
      padding-left: 2.75rem;
    }
  }
}

// Appearance Section
.cms-appearance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

// Colors Section
.cms-colors-section {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.cms-color-field {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cms-color-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: $cms-text-primary;
  margin: 0;
}

.cms-color-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.cms-color-input {
  position: relative;
  width: 60px;
  height: 60px;
  border: 2px solid $cms-border-color;
  border-radius: 12px;
  cursor: pointer;
  padding: 0;
  background: none;
  overflow: hidden;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    border-color: $cms-primary-color;
    transform: scale(1.05);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 10px;
  }
}

.cms-color-display {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  border: 2px solid $cms-border-color;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.cms-hero-preview {
  width: 100%;
  max-width: 640px;
  max-height: 280px;
  object-fit: cover;
}

.cms-hero-select {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cms-select-input {
  border: 1px solid $cms-border-color;
  border-radius: 10px;
  padding: 0.75rem;
  background: #fff;
  color: $cms-text-primary;
}

.cms-toggle-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  margin-top: 1.8rem;
}

.cms-toggle-field--card {
  margin-top: 0;
  padding: 0.75rem;
  border: 1px solid $cms-border-color;
  border-radius: 10px;
  background: $cms-background-light;
}

.cms-hero-layout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.25rem;
}

.cms-hero-card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid $cms-border-color;
  border-radius: 12px;
  background: #fff;
}

.cms-hero-card-header {
  h4 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: $cms-text-primary;
  }
}

.cms-hero-preview-wrapper {
  width: 100%;
}

.cms-hero-upload-area {
  width: 100%;
}

.cms-hero-youtube-preview {
  padding: 0.75rem;
  border: 1px dashed $cms-border-color;
  border-radius: 10px;
  background: $cms-background-light;
  overflow-wrap: anywhere;
}

.cms-hero-mobile-image {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid $cms-border-color;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cms-hero-subheading {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: $cms-text-primary;
}

.cms-trip-availability {
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  align-items: start;
  gap: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

.cms-trip-threshold-card {
  position: sticky;
  top: 5rem;
  border: 1px solid $cms-border-color;
  border-radius: 12px;
  padding: 1rem;
  background: #fff;
}

.cms-trip-threshold-preview {
  margin: 0;
  font-size: 0.8rem;
  color: $cms-text-secondary;
}

.cms-trip-messages-card {
  border: 1px solid $cms-border-color;
  border-radius: 12px;
  padding: 1rem;
  background: #fff;
  min-width: 0;
}

.cms-trip-messages-header {
  margin-bottom: 0.5rem;

  h4 {
    margin: 0 0 0.35rem 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: $cms-text-primary;
  }

  p {
    margin: 0;
    font-size: 0.8rem;
    color: $cms-text-secondary;
  }
}

.cms-trip-messages-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

.cms-trip-message-type-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.cms-trip-message-type-tab {
  border: 1px solid $cms-border-color;
  background: #fff;
  color: $cms-text-primary;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $cms-primary-color;
    color: $cms-primary-color;
  }

  &.active {
    border-color: $cms-primary-color;
    background: rgba($cms-primary-color, 0.1);
    color: $cms-primary-color;
  }
}

.cms-trip-preview-card {
  margin-top: 0.5rem;
  padding: 0.85rem;
  border: 1px dashed $cms-border-color;
  border-radius: 10px;
  background: $cms-background-light;
}

.cms-trip-preview-title {
  margin: 0 0 0.5rem 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: $cms-text-primary;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.cms-trip-preview-list {
  display: grid;
  gap: 0.45rem;

  p {
    margin: 0;
    font-size: 0.82rem;
    color: $cms-text-secondary;
  }
}


.cms-field-hint {
  font-size: 0.75rem;
  color: $cms-text-secondary;
  margin: 0;
  font-style: italic;
}

// Media Upload Section
.cms-media-field {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cms-media-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: $cms-text-primary;
  margin: 0;
}

.cms-media-upload-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cms-media-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cms-media-preview-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: $cms-background-light;
  border: 2px dashed $cms-border-color;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: $cms-primary-color;
  }

  &.cms-favicon-wrapper {
    padding: 1rem;
  }
}

.cms-media-preview-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
}

.cms-logo-preview {
  max-width: 200px;
  max-height: 100px;
  object-fit: contain;
}

.cms-favicon-preview {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.cms-media-remove-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $cms-danger-color;
  color: white;
  border: 2px solid white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(244, 67, 54, 0.4);
  }
}

.cms-media-status {
  font-size: 0.75rem;
  color: $cms-success-color;
  margin: 0;
  font-weight: 500;
}

.cms-media-upload-area {
  border: 2px dashed $cms-border-color;
  border-radius: 12px;
  padding: 1.5rem;
  background: $cms-background-light;
  transition: all 0.2s ease;

  &:hover {
    border-color: $cms-primary-color;
    background: rgba($cms-primary-color, 0.02);
  }
}

.cms-media-hint {
  font-size: 0.75rem;
  color: $cms-text-secondary;
  margin: 0.5rem 0 0 0;
  font-style: italic;
  text-align: center;

  &--block {
    text-align: left;
    font-style: normal;
    line-height: 1.45;
    margin: 0;
  }
}


// Sticky Save Button Bar
.cms-settings-save-bar {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(245, 247, 252, 0.98), rgba(245, 247, 252, 0.72));
  backdrop-filter: blur(8px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding: 0.75rem 0;
  margin-top: 2rem;
  z-index: 100;
  transition: all $cms-transition-fast;

  @media (max-width: $cms-breakpoint-mobile) {
    padding: 1rem 0;
  }
}

.cms-settings-save-content {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);

  @media (max-width: $cms-breakpoint-mobile) {
    max-width: 100%;
    padding: 0.65rem 0.75rem;
  }
}

.cms-settings-save-note {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.8rem;
  color: $cms-text-secondary;

  &.is-dirty {
    color: $cms-primary-color;
    font-weight: 600;
  }
}

.cms-settings-save-button {
  min-width: 128px;
  min-height: 46px;
  box-shadow: 0 12px 24px rgba($cms-primary-color, 0.28);
}

.cms-maintenance-layout {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.cms-maintenance-warning {
  padding: 0.9rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: $cms-primary-light;

  p {
    margin: 0;
    color: $cms-text-secondary;
    font-size: 0.9rem;
    line-height: 1.45;
  }

  &.active {
    border-color: $cms-warning-color;
    background: $cms-warning-light;
  }
}

.cms-maintenance-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 600;
}

.cms-maintenance-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cms-maintenance-bypass-card {
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #fff;
}

.cms-maintenance-bypass-header {
  margin-bottom: 0.85rem;

  h4 {
    margin: 0 0 0.35rem;
    font-size: 1rem;
  }

  p {
    margin: 0;
    color: $cms-text-secondary;
    font-size: 0.85rem;
    line-height: 1.4;
  }
}

.cms-maintenance-bypass-row {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cms-maintenance-bypass-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
</style>
